/**
 * Language Context - Multi-language support
 */
import { createContext, useContext, useState, useEffect, useCallback } from "react";
import translations from "./translations";

const LanguageContext = createContext();

const SUPPORTED_LANGUAGES = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "patois", name: "Patois", flag: "🇯🇲" },
];

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem("liantiao_language");
    return saved || "en";
  });

  useEffect(() => {
    localStorage.setItem("liantiao_language", language);
    document.documentElement.lang = language === "patois" ? "en" : language;
  }, [language]);

  const t = useCallback((key) => {
    const keys = key.split(".");
    let value = translations[language];
    
    for (const k of keys) {
      if (value && typeof value === "object") {
        value = value[k];
      } else {
        // Fallback to English
        value = translations.en;
        for (const k2 of keys) {
          if (value && typeof value === "object") {
            value = value[k2];
          }
        }
        break;
      }
    }
    
    return typeof value === "string" ? value : key;
  }, [language]);

  const changeLanguage = (lang) => {
    if (SUPPORTED_LANGUAGES.find(l => l.code === lang)) {
      setLanguage(lang);
    }
  };

  const value = {
    language,
    changeLanguage,
    t,
    languages: SUPPORTED_LANGUAGES,
    currentLanguage: SUPPORTED_LANGUAGES.find(l => l.code === language),
  };

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export default LanguageContext;
